import { observer } from "mobx-react-lite";
import { NextStepReact } from "nextstepjs";

import { playgroundTourSteps } from "@/components/Playground/tour/constants";
import { usePlaygroundTourSteps } from "@/components/Playground/tour/hooks/usePlaygroundTourSteps";
import { useTourScrollLock } from "@/components/Playground/tour/hooks/useTourScrollLock";
import { useTourUrlParam } from "@/components/Playground/tour/hooks/useTourUrlParam";
import {
  useOptionalPlaygroundTourContext,
  usePlaygroundTourContext,
} from "@/components/Playground/tour/context";

/**
 * Inner bridge: lives inside both the PlaygroundTourProvider AND the
 * PlaygroundStoreProvider. Calls all the hooks that need access to those
 * contexts (URL param detection, NextStep callbacks) and renders
 * `<NextStepReact>` with the wired callbacks.
 *
 * Wrapped in `observer` so MobX reads inside `useTourUrlParam` (notably
 * `playground.isPendingSessionInitialization`) subscribe this component to
 * MobX changes — without that, the URL effect would never re-fire when
 * the playground session finishes initializing and the tour would never
 * actually start on a fresh page load.
 *
 * Renders an empty `nextstep-site` wrapper because NextStepReact requires
 * children. The actual playground UI is rendered by `PlaygroundCore`'s
 * sibling content — the spotlight overlay is portaled to document.body.
 */
const PlaygroundTourBridgeInner = observer(() => {
  const tour = usePlaygroundTourContext();

  // Initialize URL param detection (auto-starts the tour if `?tour` is
  // present) and grab `removeTourParam` for the Skip/Finish handlers.
  // Called once at this level — `usePlaygroundTourSteps` no longer calls
  // it internally so the start effect doesn't run twice per render.
  const { removeTourParam } = useTourUrlParam();

  // Lock user-initiated scrolling on the messages and sidebar containers
  // (any element with `data-tour-lock-scroll`) while the tour is running.
  // Programmatic scroll via `scrollIntoView` continues to work — only user
  // wheel/touch gestures are blocked.
  useTourScrollLock(tour.isActive);

  const { handleStart, handleStepChange, handleComplete, handleSkip } =
    usePlaygroundTourSteps(removeTourParam);

  return (
    <NextStepReact
      steps={playgroundTourSteps}
      onStart={handleStart}
      onStepChange={handleStepChange}
      onComplete={handleComplete}
      onSkip={handleSkip}
      shadowRgb="0, 0, 0"
      shadowOpacity="0.45"
      cardTransition={{ ease: "easeOut", duration: 0.16 }}
      noInViewScroll
      scrollToTop={false}
      disableConsoleLogs
    >
      <span aria-hidden />
    </NextStepReact>
  );
});

/**
 * Outer bridge: safe to render anywhere inside `PlaygroundCore`. If the
 * tour provider is not present (e.g. when PlaygroundCore renders inside the
 * PromptTemplate page), this is a no-op.
 */
const PlaygroundTourBridge = () => {
  const tour = useOptionalPlaygroundTourContext();
  if (!tour) {
    return null;
  }
  return <PlaygroundTourBridgeInner />;
};

export default PlaygroundTourBridge;
